import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import { EASE_OUT, MUTED_GRAY, NOPING_GREEN, PURPLE, TEXT_LIGHT } from './constants';

export function ScanlineOverlay({ className = '' }: { className?: string }) {
  return <div className={`pointer-events-none absolute inset-0 ${className}`} style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(147,255,24,0.015) 2px, rgba(147,255,24,0.015) 4px)' }} aria-hidden='true' />;
}

export function ThemedCard({ children, className = '', delay = 0, glow = NOPING_GREEN }: { children: React.ReactNode; className?: string; delay?: number; glow?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} transition={{ duration: 0.6, delay, ease: EASE_OUT }} className={`relative rounded-xl border backdrop-blur-sm ${className}`} style={{ background: `linear-gradient(180deg, ${PURPLE}90, ${PURPLE}40)`, borderColor: `${glow}25`, boxShadow: `0 0 30px ${glow}08, inset 0 1px 0 ${glow}10` }}>
      <ScanlineOverlay />
      <div className='relative z-10 flex flex-1 flex-col'>{children}</div>
    </motion.div>
  );
}

export function Section({ children, className = '', id, bg }: { children: React.ReactNode; className?: string; id?: string; bg?: React.ReactNode }) {
  return (
    <section id={id} className={`relative px-4 py-14 md:px-8 md:py-20 ${className}`} style={{ zIndex: 1 }}>
      {bg}
      <div className='relative mx-auto max-w-6xl' style={{ zIndex: 1 }}>
        {children}
      </div>
    </section>
  );
}

export function SectionTitle({ subtitle, title, titleAccent, description, accentColor = NOPING_GREEN }: { subtitle: string; title: string; titleAccent?: string; description?: string; accentColor?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.5, ease: EASE_OUT }} className='mb-8 text-center md:mb-12'>
      <span className='mb-3 block text-xs font-bold uppercase tracking-[0.25em] md:text-sm' style={{ color: accentColor, fontFamily: 'var(--font-bai)' }}>
        {subtitle}
      </span>
      <h2 className='mb-4 text-3xl font-bold md:text-4xl lg:text-5xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
        {title}
        {titleAccent && (
          <>
            <br />
            <span style={{ color: accentColor }}>{titleAccent}</span>
          </>
        )}
      </h2>
      {description && (
        <p className='mx-auto max-w-2xl text-base md:text-lg' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
          {description}
        </p>
      )}
    </motion.div>
  );
}
